import { Copy, Download, FilePlus2, LoaderCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { apiClient, type CompanyContext } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { SearchSelect } from "@/shared/components/SearchSelect";
import { activeCompanies } from "@/shared/utils/companies";
import { configFilename } from "@/features/settings/ConfigExportButton";
import { downloadBlob } from "@/features/uploads/upload-shared";

/**
 * "De onde tirar a planilha": o modelo em branco (rota sem empresa) e a
 * configuração de uma empresa que a pessoa já acessa, pra editar e subir
 * como a nova. A lista é a do `/me` que a casca já tem (só ATIVAS — a
 * sombra de concorrente nem chega ali), com busca local. O servidor
 * confere o acesso à empresa escolhida: o `X-Company-Id` vai POR CHAMADA,
 * nunca o da empresa ativa.
 *
 * Falha de AÇÃO não troca a página: o aviso aparece aqui, ao lado.
 */
export function ConfigSources({
  token,
  companies
}: {
  token: string;
  companies: CompanyContext[];
}) {
  const [chosenId, setChosenId] = useState("");
  const [busy, setBusy] = useState<"template" | "company" | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const options = useMemo(
    () => activeCompanies(companies).map(company => ({ value: company.id, label: company.name })),
    [companies]
  );

  const run = async (kind: "template" | "company") => {
    setBusy(kind);
    setFailure(null);
    try {
      if (kind === "template") {
        downloadBlob(await apiClient.downloadCompanyConfigTemplate(token), "modelo-configuracao.xlsx");
      } else {
        const chosen = options.find(option => option.value === chosenId);
        downloadBlob(
          await apiClient.downloadCompanyConfig(token, { companyId: chosenId }),
          configFilename(chosen?.label)
        );
      }
    } catch (error) {
      setFailure(errorMessage(error, "Não foi possível baixar a planilha."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="config-sources">
      {/* Dois cartões da mesma altura, com o botão no pé de cada um: as
          duas saídas ficam lado a lado, alinhadas, em vez de um botão
          solto em cima de uma caixa com outro botão embaixo. */}
      <div className="config-source-card">
        <span className="config-source-icon" aria-hidden="true">
          <FilePlus2 size={18} />
        </span>
        <strong>Modelo em branco</strong>
        <p>Todas as abas vazias, com o cabeçalho certo — para montar o cadastro do zero no Excel.</p>
        <button
          type="button"
          className="secondary-button"
          disabled={busy !== null}
          onClick={() => void run("template")}
        >
          {busy === "template" ? <LoaderCircle className="spin" size={16} /> : <Download size={16} />}
          Baixar modelo em branco
        </button>
      </div>
      {options.length > 0 && (
        <div className="config-source-card">
          <span className="config-source-icon" aria-hidden="true">
            <Copy size={18} />
          </span>
          <strong>Copiar de uma empresa</strong>
          <p>A configuração completa de uma empresa que você acessa — edite só o que muda.</p>
          <SearchSelect
            label="Empresa"
            value={chosenId}
            options={options}
            onChange={setChosenId}
            allLabel="Escolha uma empresa"
          />
          <button
            type="button"
            className="secondary-button"
            disabled={busy !== null || !chosenId}
            onClick={() => void run("company")}
          >
            {busy === "company" ? <LoaderCircle className="spin" size={16} /> : <Download size={16} />}
            Baixar configuração
          </button>
        </div>
      )}
      {failure && (
        <p className="form-error config-sources-error" role="alert">{failure}</p>
      )}
    </div>
  );
}
